import { useEffect, useState } from 'react';
import { biometricUnlock, storedSession } from '@matix/ui';
import { saveAuth, type AuthData } from '../../lib/cashierAuthClient';
import { useLang } from '../../lib/language';

/**
 * «الدخول بالبصمة» under the sign-in button, inside the phone shell only.
 *
 * It renders nothing at all unless a usable session is actually in the device
 * keystore — in a browser, with biometrics off, or once the stored session's
 * 8-hour token has expired, there is no button and the screen is the one the
 * till has always had.
 *
 * On success the session is written back under the key `cashierAuthClient`
 * reads, then the page reloads. The reload is deliberate: it is the same path
 * an ordinary refresh takes, so the till boots through its normal sequence
 * instead of a second, parallel way of becoming signed in. The cold-start lock
 * does not fire again — `sessionStorage` already marks this run as started.
 */
export function BiometricUnlock(): JSX.Element | null {
  const { lang } = useLang();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void storedSession().then((s) => {
      if (alive) setAvailable(s !== null);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!available) return null;

  const label = lang === 'ar' ? 'الدخول بالبصمة' : 'Sign in with biometrics';

  return (
    <button
      type="button"
      className="tkt-submit"
      style={{ marginTop: 10, background: 'transparent', color: 'inherit', border: '1px solid currentColor' }}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void biometricUnlock({
          reason: lang === 'ar' ? 'للدخول إلى Matix POS' : 'Sign in to Matix POS',
          cancel: lang === 'ar' ? 'إلغاء' : 'Cancel',
          title: lang === 'ar' ? 'الدخول بالبصمة' : 'Biometric sign-in',
        })
          .then((session) => {
            if (!session) {
              setBusy(false);
              return;
            }
            try {
              saveAuth(JSON.parse(session) as AuthData);
              window.location.reload();
            } catch {
              setBusy(false);
            }
          })
          .catch(() => setBusy(false));
      }}
    >
      <span>{busy ? (lang === 'ar' ? 'جارٍ…' : 'Working…') : label}</span>
    </button>
  );
}
